import { useState, useEffect, useCallback, useMemo } from 'react';
import { GET_MEALS } from 'store/queries';
import { UPDATE_MEALS } from 'store/mutations';
import { useLazyQuery, useMutation } from '@apollo/client';
import { customMealCode, regularMeal } from 'types/meals';
import { INBOUND, OUTBOUND } from 'constants/booking';
import { isNoMealClass, isNoMealRoute } from 'utils/meals';

export const useMeals = ({ booking }) => {
  const [getOutboundMeals, allOutboundMeals] = useLazyQuery(GET_MEALS);
  const [getInboundMeals, allInboundMeals] = useLazyQuery(GET_MEALS);
  const [updateMeals] = useMutation(UPDATE_MEALS);
  const [availableMeals, setAvailableMeals] = useState({});
  const [mealsToUpdate, setMealsToUpdate] = useState({
    outbound: [],
    inbound: []
  });
  const [isDone, setIsDone] = useState(false);

  const newSegments = useMemo(
    () => ({
      outbound:
        booking?.pendingCreationLegs?.filter(({ serviceType, direction }) => serviceType.code === 'ES' && direction === OUTBOUND) ?? [],
      inbound:
        booking?.pendingCreationLegs?.filter(({ serviceType, direction }) => serviceType.code === 'ES' && direction === INBOUND) ?? []
    }),
    [booking?.pendingCreationLegs]
  );

  const oldSegments = useMemo(
    () => ({
      outbound:
        booking?.pendingCancellationLegs?.filter(({ serviceType, direction }) => serviceType.code === 'ES' && direction === OUTBOUND) ?? [],
      inbound:
        booking?.pendingCancellationLegs?.filter(({ serviceType, direction }) => serviceType.code === 'ES' && direction === INBOUND) ?? []
    }),
    [booking?.pendingCancellationLegs]
  );

  useEffect(() => {
    if (!newSegments?.outbound?.length) {
      return;
    }

    getOutboundMeals({
      variables: {
        reference: booking.reference,
        segmentId: newSegments.outbound[0].id
      }
    });
  }, [getOutboundMeals, newSegments?.outbound, booking.reference]);

  useEffect(() => {
    if (!newSegments?.inbound?.length) {
      return;
    }

    getInboundMeals({
      variables: {
        reference: booking.reference,
        segmentId: newSegments.inbound[0].id
      }
    });
  }, [getInboundMeals, newSegments?.inbound, booking.reference]);

  useEffect(() => {
    const isNowDone =
      (newSegments?.outbound?.length ? allOutboundMeals.called && !allOutboundMeals.loading : true) &&
      (newSegments?.inbound?.length ? allInboundMeals.called && !allInboundMeals.loading : true);

    if (isDone !== isNowDone) {
      setIsDone(isNowDone);
    }

    if (isNowDone && !isDone) {
      const legs = [...(booking.pendingCreationLegs || [])];

      const outboundMeals = allOutboundMeals.data?.meals || [];
      const inboundMeals = allInboundMeals.data?.meals || [];

      const meals = legs.reduce((acc, leg) => {
        const mealsFromDirection = leg.direction === 'outbound' ? outboundMeals : inboundMeals;

        acc[leg.id] = leg.serviceType.code === 'ES' ? [...mealsFromDirection] : [];

        const isNoMealCallOfService = isNoMealClass(leg.classOfService.code);
        const isNoMealLeg = isNoMealRoute(leg.origin?.uic, leg.destination?.uic);

        if (isNoMealCallOfService || isNoMealLeg) {
          acc[leg.id].push({
            code: customMealCode.NO_MEAL,
            name: customMealCode.NO_MEAL
          });
        } else {
          acc[leg.id].push({
            code: regularMeal.code,
            name: regularMeal.name
          });
        }

        return acc;
      }, {});

      setAvailableMeals(meals);
    }
  }, [booking, newSegments?.outbound, allOutboundMeals, newSegments?.inbound, allInboundMeals, isDone, setIsDone, setAvailableMeals]);

  useEffect(() => {
    if (Object.keys(availableMeals).length) {
      const segments = oldSegments?.outbound?.length > 0 || oldSegments?.inbound?.length > 0 ? oldSegments : newSegments;

      const outboundMeals = segments?.outbound?.flatMap(({ meals }) => meals || []) || [];
      const inboundMeals = segments?.inbound?.flatMap(({ meals }) => meals || []) || [];

      const outboundClassOfServiceCode = newSegments?.outbound?.[0]?.classOfService.code;
      const inboundClassOfServiceCode = newSegments?.inbound?.[0]?.classOfService.code;

      const firstOutboundSegmentId = newSegments?.outbound?.[0]?.id;
      const firstInboundSegmentId = newSegments?.inbound?.[0]?.id;

      const initMeal = (code, segmentId) => (meal) => {
        const defaultValue = isNoMealClass(code) ? customMealCode.NO_MEAL : customMealCode.REGULAR_MEAL;

        return {
          passengerId: meal.passenger.id,
          mealCode: availableMeals?.[segmentId]?.find((availableMeal) => availableMeal.code === meal.code) ? meal.code : defaultValue
        };
      };

      setMealsToUpdate({
        outbound: outboundMeals.map(initMeal(outboundClassOfServiceCode, firstOutboundSegmentId)),
        inbound: inboundMeals.map(initMeal(inboundClassOfServiceCode, firstInboundSegmentId))
      });
    }
  }, [availableMeals, oldSegments, newSegments]);

  const setMealsForPassenger = useCallback(
    ({ direction, passengerId, mealCode }) => {
      setMealsToUpdate({
        ...mealsToUpdate,
        [direction]: mealsToUpdate[direction].map((meal) => {
          if (meal.passengerId === passengerId) {
            return {
              ...meal,
              mealCode: mealCode
            };
          }

          return meal;
        })
      });
    },
    [mealsToUpdate, setMealsToUpdate]
  );

  const saveMeals = useCallback(async () => {
    const outboundNewMeals = newSegments?.outbound?.flatMap(({ meals }) => meals || []) || [];
    const inboundNewMeals = newSegments?.inbound?.flatMap(({ meals }) => meals || []) || [];

    const outboundMealsToUpdate = prepareMealCodes(mealsToUpdate.outbound, outboundNewMeals);
    const inboundMealsToUpdate = prepareMealCodes(mealsToUpdate.inbound, inboundNewMeals);

    const hasNoMealRoute =
      isNoMealRoute(newSegments.outbound?.[0]?.origin?.uic, newSegments.outbound?.[0]?.destination?.uic) ||
      isNoMealRoute(newSegments.inbound?.[0]?.origin?.uic, newSegments.inbound?.[0]?.destination?.uic);

    // New segment returns only ES legs
    if (!outboundMealsToUpdate.length && !inboundMealsToUpdate.length && hasNoMealRoute) {
      return;
    }

    await updateMeals({
      variables: {
        bookingReference: booking.reference,
        outbound: outboundMealsToUpdate,
        inbound: inboundMealsToUpdate
      }
    });
  }, [booking.reference, newSegments?.outbound, newSegments?.inbound, mealsToUpdate, updateMeals]);

  return {
    isDone,
    mealsToUpdate,
    setMealsForPassenger,
    availableMeals,
    saveMeals
  };
};

const prepareMealCodes = (selectedMeals, products) => {
  const updateMeals = [];
  const nullMealCodes = [customMealCode.REGULAR_MEAL, customMealCode.NO_MEAL];

  selectedMeals.forEach((meal) => {
    const product = products.find((product) => meal.passengerId === product.passenger.id);

    if (product && nullMealCodes.includes(meal.mealCode)) {
      updateMeals.push({
        passengerId: meal.passengerId,
        mealCode: null
      });
    } else if ((!product && !nullMealCodes.includes(meal.mealCode)) || product) {
      updateMeals.push({
        passengerId: meal.passengerId,
        mealCode: meal.mealCode
      });
    }
  });

  return updateMeals;
};
